//from : https://github.com/mrdoob/three.js/blob/master/src/geometries/PolyhedronGeometry.js

import GeometryBuffer from './GeometryBuffer';
import * as vec3 from './glMatrix/vec3';

function azimuth( vector ) {
    return Math.atan2( vector[2], - vector[0] );
}

function inclination( vector ) {
    return Math.atan2( - vector[1], Math.sqrt( ( vector[0] * vector[0] ) + ( vector[2] * vector[2] ) ) );
}

function lerp( out, v, alpha ) {
    out[0] += ( v[0] - out[0] ) * alpha;
    out[1] += ( v[1] - out[1] ) * alpha;
    out[2] += ( v[2] - out[2] ) * alpha;
    return out;
}

class IcoSphereGeometryBuffer extends GeometryBuffer {
	
	constructor(gl, options) {

		super(gl, 0.);

		options = Object.assign({},{
			radius:  50,
			details: 0,
		}, options);

        if (!gl) {
            console.warn('missing gl')
            return;
        }
        
        
        const vertexBuffer = [];
        
        const t = ( 1 + Math.sqrt( 5 ) ) / 2;
		const vertices = [
			- 1, t, 0, 	1, t, 0, 	- 1, - t, 0, 	1, - t, 0,
			0, - 1, t, 	0, 1, t,	0, - 1, - t, 	0, 1, - t,
			t, 0, - 1, 	t, 0, 1, 	- t, 0, - 1, 	- t, 0, 1
		];
		const indices = [
			0, 11, 5, 	0, 5, 1, 	0, 1, 7, 	0, 7, 10, 	0, 10, 11,
			1, 5, 9, 	5, 11, 4,	11, 10, 2,	10, 7, 6,	7, 1, 8,
			3, 9, 4, 	3, 4, 2,	3, 2, 6,	3, 6, 8,	3, 8, 9,
			4, 9, 5, 	2, 4, 11,	6, 2, 10,	8, 6, 7,	9, 8, 1
		];


        function getVertexByIndex( index, vertex ) {
            const stride = index * 3;
            vertex[0] = vertices[ stride + 0 ];
            vertex[1] = vertices[ stride + 1 ];
            vertex[2] = vertices[ stride + 2 ];
        }

      
        function subdivideFace( a, b, c, detail ) {
            const cols = detail + 1;
            const v = [];
            for ( let i = 0; i <= cols; i ++ ) {
                v[ i ] = [];
                const aj = vec3.create();
                const bj = vec3.create();
                vec3.copy( aj, a );
                vec3.copy( bj, b );
                lerp( aj, c, i / cols );
                lerp( bj, c, i / cols );
                const rows = cols - i;
                for ( let j = 0; j <= rows; j ++ ) {
                    if ( j === 0 && i === cols ) {
                        v[ i ][ j ] = aj;
                    } else {
                        const cln = vec3.create();
                        vec3.copy(cln, aj);
                        v[ i ][ j ] = lerp( cln, bj, j / rows );
                    }
                }
            }
            for ( let i = 0; i < cols; i ++ ) {
                for ( let j = 0; j < 2 * ( cols - i ) - 1; j ++ ) {
                    const k = Math.floor( j / 2 );
                    if ( j % 2 === 0 ) {
                        pushVertex( v[ i ][ k + 1 ] );
                        pushVertex( v[ i + 1 ][ k ] );
                        pushVertex( v[ i ][ k ] );
                    } else {
                        pushVertex( v[ i ][ k + 1 ] );
                        pushVertex( v[ i + 1 ][ k + 1 ] );
                        pushVertex( v[ i + 1 ][ k ] );
                    }
                }
            }
        }

        function pushVertex( vertex ) {
            vertexBuffer.push( vertex[0], vertex[1], vertex[2] );
        }

        const tmp_vertex = vec3.create();

        // the subdivision creates the vertex buffer data
        const a = vec3.create();
        const b = vec3.create();
        const c = vec3.create();

        // iterate over all faces and apply a subdivision with the given detail value
        for ( let i = 0; i < indices.length; i += 3 ) {
            // get the vertices of the face
            getVertexByIndex( indices[ i + 0 ], a );
            getVertexByIndex( indices[ i + 1 ], b );
            getVertexByIndex( indices[ i + 2 ], c );
            // perform subdivision
            subdivideFace( a, b, c, options.details );
        }

        // iterate over the entire buffer and apply the radius to each vertex
        for ( let i = 0; i < vertexBuffer.length; i += 3 ) {
            tmp_vertex[0] = vertexBuffer[ i + 0 ];
            tmp_vertex[1] = vertexBuffer[ i + 1 ];
            tmp_vertex[2] = vertexBuffer[ i + 2 ];
            vec3.normalize( tmp_vertex, tmp_vertex );
            vec3.scale( tmp_vertex, tmp_vertex, options.radius );
            vertexBuffer[ i + 0 ] = tmp_vertex[0];
            vertexBuffer[ i + 1 ] = tmp_vertex[1];
            vertexBuffer[ i + 2 ] = tmp_vertex[2];
        }

		// buffers
		var normals  = [];
		var uvs      = [];
		var colors   = [];

        
        for ( let i = 0; i < vertexBuffer.length; i += 3 ) {
            tmp_vertex[0] = vertexBuffer[ i + 0 ];
            tmp_vertex[1] = vertexBuffer[ i + 1 ];
            tmp_vertex[2] = vertexBuffer[ i + 2 ];
            const u = azimuth( tmp_vertex ) / 2 / Math.PI + 0.5;
            const v = inclination( tmp_vertex ) / Math.PI + 0.5;

            const n = vec3.create();
            vec3.copy(n, tmp_vertex);
            vec3.normalize(n,n);
            uvs.push( u, 1 - v );
            normals.push(n[0],n[1],n[2]);
            colors.push(0,0,0);
        }

		// build geometry
		this.length = vertexBuffer.length/3;

		this.addAttribute( 'position', new Float32Array( vertexBuffer ), 3 );
		this.addAttribute( 'normal',   new Float32Array( normals  ), 3 );
		this.addAttribute( 'color',    new Float32Array( colors  ), 3 );
		this.addAttribute( 'uv',       new Float32Array( uvs      ), 2, 'IcoSphere' );

	}

}

export default IcoSphereGeometryBuffer;